import { useApiClient } from "../apiClients";
import { QUEST_REVIEW_ENDPOINTS } from "./endpoints";
import type { UUID } from "../../models/domain";

/**
 * La file « Quêtes à corriger » (tc_learn `api/routes/admin_bodies.py`).
 *
 * Une entrée est un CORPS partagé (un exercice servi en clone à plusieurs
 * élèves) ou une QUÊTE signalée qui n'a pas de corps partagé. Elle arrive
 * jointe : le contrat, les signalements (réponse de l'élève contre la clé),
 * les verdicts des relecteurs, et les faits que la génération a reçus.
 */

/** Pourquoi un corps est hors partage (`quest_body.disabled_reason`). */
export type DisabledReason = "audit" | "report_review" | "reporters" | "revalidation" | "human";
/**
 * Pourquoi un corps TOUJOURS SERVI attend un regard humain
 * (`quest_body.human_queue_reason`, tc_learn lot L4) : un texte jugé faux par
 * la vérification des textes. Ce n'est pas un retrait du partage.
 */
export type HumanQueueReason = "text_suspect";
/** Décision humaine (`quest_body.human_status`). */
export type HumanStatus = "validated" | "corrected" | "rejected";

export interface ReportAnswer {
  step_id: string;
  render_type?: string | null;
  prompt?: string | null;
  /** La clé, telle que le serveur la lit (option correcte, ou spec attendue). */
  expected?: string | null;
  /** Ce que l'élève a posé. */
  given?: string | null;
  correct?: boolean | null;
}

export interface QueueReport {
  id: number;
  quest_id: UUID;
  reason: "exercise_wrong" | "narration_incoherent" | "visual_wrong" | "other" | string;
  comment?: string | null;
  reporter_id: number;
  created_at: string;
  answers: ReportAnswer[];
}

export interface QueueVerdict {
  /** body_text_suspect, body_suspended_review, body_suspended_reporters… */
  type: string;
  reason?: string | null;
  model?: string | null;
  at?: string | null;
}

export interface QueueFacts {
  /** Identifiants OPAQUES, à résoudre dans l'unité `neighborhood` de l'objectif. */
  fact_ids: string[];
  /** `portée:version:id` du fait que la question interroge (QCM v3). */
  anchor_fact_id?: string | null;
  /** `source_hash[:8]` de la base de faits au moment de la génération. */
  knowledge_version: string;
  objective_title: string;
}

/** Le contrat de quête tel que stocké (sans `quest_id`, prénom en jeton). */
export type QuestBody = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export interface QueueEntry {
  kind: "body" | "quest";
  /** `body_id` ou `quest_id` selon `kind`. */
  id: string;
  objective_id?: UUID | null;
  document_id?: UUID | null;
  template_id?: string | null;
  language?: string;
  intent?: string;
  created_at?: string;
  uses?: number;
  disabled_at?: string | null;
  disabled_reason?: DisabledReason | null;
  human_queue_reason?: HumanQueueReason | null;
  human_status?: HumanStatus | null;
  body: QuestBody | null;
  facts: QueueFacts;
  reports: QueueReport[];
  reporters: number;
  verdicts: QueueVerdict[];
}

export interface QueueResponse {
  total: number;
  reported_bodies: number;
  reported_quests: number;
  flagged_bodies: number;
  entries: QueueEntry[];
}

export interface DecisionResponse {
  status: string;
  action: "validate" | "reject" | "correct";
  body_id: string;
  /** Le corps servi désormais (le nouveau, après une correction). */
  served_body_id: string | null;
  closed_reports: number[];
}

export interface DocumentQuality {
  document_id: UUID | null;
  reported_quests: number;
  pending_reports: number;
  rejected_bodies: number;
  top_facts: Array<{ objective_id: UUID | null; fact_id: string; count: number }>;
}

/** Message d'une erreur tc_learn (`detail`, texte ou `{violations}`). */
export const learnErrorMessage = (e: unknown, fallback = "Une erreur est survenue."): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err?.response?.data?.detail as { violations?: string[] } | string | undefined;
  if (typeof detail === "string") return detail;
  if (detail && Array.isArray(detail.violations)) return detail.violations.join(" ; ");
  return err?.message || fallback;
};

export const useQuestReviewApi = () => {
  const apiClient = useApiClient("learn");

  const getQueue = (limit = 30) =>
    apiClient.get<QueueResponse>(QUEST_REVIEW_ENDPOINTS.QUEUE, { params: { limit } });

  const validateBody = (bodyId: string) =>
    apiClient.post<DecisionResponse>(QUEST_REVIEW_ENDPOINTS.VALIDATE(bodyId));

  const rejectBody = (bodyId: string) =>
    apiClient.post<DecisionResponse>(QUEST_REVIEW_ENDPOINTS.REJECT(bodyId));

  const correctBody = (bodyId: string, body: QuestBody) =>
    apiClient.post<DecisionResponse>(QUEST_REVIEW_ENDPOINTS.CORRECT(bodyId), { body });

  const closeQuest = (questId: string, resolution: "dismissed" | "fixed") =>
    apiClient.post<{ closed_reports: number[] }>(QUEST_REVIEW_ENDPOINTS.CLOSE_QUEST(questId), { resolution });

  const getQualityByDocument = (days = 30) =>
    apiClient.get<{ days: number; documents: DocumentQuality[] }>(QUEST_REVIEW_ENDPOINTS.BY_DOCUMENT, {
      params: { days },
    });

  return { getQueue, validateBody, rejectBody, correctBody, closeQuest, getQualityByDocument };
};
